import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Feature, FeatureCollection } from "geojson";

import { api } from "../../api/client";
import type { Annotation, AnnotationIdentity, Phase } from "../../api/types";
import { t } from "../../i18n/i18n";
import { refuseIfDossierLocked, useDossierLocked } from "../../sharing/access";
import { annotationControl, type ControlKey, useUi } from "../../state/ui";
import { annotationsOf, dossierWrite, recordWrite, undoAction, useDossierHistory, writeFailure } from "../dossierWrites";
import { orderActions, ordersNote } from "../orderSync";
import { useDeployment } from "../units/useDeployment";
import { type AnnotationCategoryKey, categoryOf, labelled, sidcOf, symbolOf } from "./catalog";
import { annotationImageId } from "./symbolStyle";

/** Annotations APP-6 du dossier (conception du 15/10/2026) : lecture, pose, fiche, déplacement par temps et
 *  suppression. Chaque modification enregistre l'état complet (`PUT …/annotations`) ; l'OPO suit la carte (les
 *  ordres sont recomposés, cause « carte ») ; chaque action s'inscrit dans l'historique de la carte (canal
 *  « annotations ») et « Annuler » la défait. */

export const PHASES: readonly Phase[] = ["T1", "T2", "T3"];
export const MAX_ANNOTATIONS = 300;

export { annotationsOf };

/** Position [lon, lat] au temps `phase` : la sienne, sinon celle du temps précédent le plus proche. */
export function positionAt(item: Annotation, phase: Phase): [number, number] {
  const index = PHASES.indexOf(phase);
  for (let i = index; i >= 0; i--) {
    const own = item.positions[PHASES[i]!];
    if (own) return own;
  }
  return item.positions.T1;
}

/** L'annotation paraît-elle au temps `phase` (temps cochés de sa fiche) ? */
export function visibleAt(item: Annotation, phase: Phase): boolean {
  return (item.phases?.length ? item.phases : PHASES).includes(phase);
}

/** Annotation reprise du chef (cascade vers les échelons subordonnés) : lecture seule, seul le dossier du chef la
 *  déplace, la modifie ou la supprime. */
export function inherited(item: Annotation): boolean {
  return Boolean(item.origin);
}

/** Mention d'une annotation reprise du chef (« Posée par l'échelon supérieur (1erRTEST) »). */
export function originNote(item: Annotation): string {
  return item.origin_label
    ? t("Posée par l'échelon supérieur ({unit})", { unit: item.origin_label })
    : t("Posée par l'échelon supérieur");
}

/** Nom affiché : celui saisi, sinon le nom de son type. */
export function nameOf(item: Annotation): string {
  const symbol = symbolOf(item.key);
  return item.label || (symbol ? labelled(symbol) : item.key);
}

/** Texte porté à côté du symbole (« PRESSE »), dans la langue de l'interface. */
export function amplifierOf(key: string): string | null {
  const text = symbolOf(key)?.text;
  return text ? labelled(text) : null;
}

/** Libellé automatique d'un point de contrôle numéroté (« CKP 3 » après « CKP 1 » et « CKP 2 ») ; null sans sigle. */
export function nextLabel(items: readonly Annotation[], key: string): string | null {
  const short = symbolOf(key)?.short;
  if (!short) return null;
  const pattern = new RegExp(`^${short} (\\d+)$`);
  const used = items.map((a) => pattern.exec(a.label ?? "")).filter((m): m is RegExpExecArray => m !== null).map((m) => Number(m[1]));
  return `${short} ${Math.max(0, ...used) + 1}`;
}

function newId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID().toUpperCase();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`.toUpperCase();
}

/** Nouvelle annotation d'un type posée en `at` : identité par défaut, tous les temps, site protégé par défaut. */
export function newAnnotation(key: string, at: [number, number], items: readonly Annotation[], id = newId()): Annotation {
  const symbol = symbolOf(key);
  if (!symbol) throw new Error(`Annotation inconnue : ${key}`);
  const label = nextLabel(items, key);
  const item: Annotation = {
    id,
    key,
    identity: symbol.default_identity as AnnotationIdentity,
    sidc: sidcOf(key),
    positions: { T1: at },
    phases: [...PHASES],
    created_at: Date.now() / 1000,
  };
  if (label) item.label = label;
  if (categoryOf(key)?.key === "infrastructure") item.protected = Boolean(symbol.protected);
  return item;
}

/** Annotation déplacée au temps `phase` : sa position de ce temps (les temps suivants sans position propre suivent). */
export function movedTo(item: Annotation, phase: Phase, at: [number, number]): Annotation {
  return { ...item, positions: { ...item.positions, [phase]: at } };
}

/** Modification d'une fiche : champs vides retirés, SIDC recalculé (identité, échelon). */
export function patched(item: Annotation, patch: Partial<Annotation>): Annotation {
  const next = { ...item, ...patch } as Annotation & Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value === null || value === "") delete next[key];
  }
  next.sidc = sidcOf(next.key, next.identity, next.echelon ?? null);
  return next;
}

/** Points de la carte au temps affiché : catégories visibles seulement, annotation glissée à sa place proposée. */
export function annotationFeatures(
  items: readonly Annotation[],
  phase: Phase,
  visible: Readonly<Partial<Record<ControlKey, boolean>>>,
  shift: { id: string; lngLat: [number, number] } | null = null,
): FeatureCollection {
  const features: Feature[] = [];
  for (const item of items) {
    const category = categoryOf(item.key)?.key as AnnotationCategoryKey | undefined;
    if (!category || visible[annotationControl(category)] === false || !visibleAt(item, phase)) continue;
    const at = shift?.id === item.id ? shift.lngLat : positionAt(item, phase);
    const sidc = item.sidc ?? sidcOf(item.key, item.identity, item.echelon ?? null);
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: at },
      properties: {
        id: item.id,
        key: item.key,
        category,
        icon: annotationImageId(sidc, amplifierOf(item.key)),
        label: item.label ?? "",
        inherited: inherited(item),
      },
    });
  }
  return { type: "FeatureCollection", features };
}

interface Change {
  apply: (items: Annotation[]) => Annotation[];
  /** Avis après enregistrement (traduit). */
  text: () => string;
  /** Action inscrite dans l'historique, en minuscule (« ajout de CKP 1 »). */
  label: () => string;
  /** Annotation à sélectionner une fois enregistrée (pose), et à désigner (champ « Nom »). */
  select?: { id: string; at: [number, number]; focus?: boolean };
}

export function useAnnotations() {
  const queryClient = useQueryClient();
  const params = useUi((s) => s.deploymentParams);
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const select = useUi((s) => s.select);
  const { data: deployment } = useDeployment();
  const items = annotationsOf(deployment);
  const locked = useDossierLocked();
  const canEdit = Boolean(params?.exercise_id) && !locked;
  useDossierHistory();

  const mutation = useMutation({
    mutationFn: (change: Change) =>
      dossierWrite(queryClient, (target, opo, current) => api.setAnnotations(target, change.apply(annotationsOf(current)), opo), {
        notSaved: t("Enregistrez le dossier pour poser des annotations."),
        channel: "annotations",
      }),
    onSuccess: (write, change) => {
      const ui = useUi.getState();
      const selected = ui.selection?.layer === "annotation" ? ui.selection.id : null;
      if (change.select) {
        select({ layer: "annotation", id: change.select.id, lngLat: change.select.at });
        if (change.select.focus) ui.setAnnotationFocus(change.select.id);
      } else if (selected && !annotationsOf(write.result).some((a) => a.id === selected)) {
        select(null);
      }
      const recorded = recordWrite(write, change.label());
      notify({
        text: change.text(),
        detail: ordersNote(write.result, { centac: Boolean(write.result.centac) }) || t("Annotations enregistrées dans le dossier."),
        tone: "ok",
        ...orderActions(undoAction(recorded), write.result),
      });
    },
    onError: (error) => writeFailure(queryClient, error, t("Annotation non enregistrée")),
  });

  /** Sans dossier enregistré, « Enregistrer » (ou « Créer le dossier ») des Données initiales clignote. */
  const guard = (action: string): boolean => {
    if (refuseIfDossierLocked()) return false;
    if (useUi.getState().deploymentParams?.exercise_id) return true;
    requestSave();
    notify({
      text: exerciseId
        ? t("Enregistrez les modifications du dossier pour {action}", { action })
        : t("Créez le dossier pour {action}", { action }),
      detail: t("Les annotations sont mémorisées dans le dossier d'exercice."),
      tone: "info",
    });
    return false;
  };

  /** Annotation reprise du chef : l'avis le dit, rien n'est écrit (lecture seule). */
  const refuseInherited = (id: string): boolean => {
    const found = items.find((a) => a.id === id);
    if (!found || !inherited(found)) return false;
    notify({
      text: t("Lecture seule : {note}", { note: originNote(found) }),
      detail: t("Seul le dossier de l'échelon supérieur la modifie. Vous pouvez la masquer dans le panneau Calques."),
      tone: "info",
    });
    return true;
  };

  /** Pose d'un symbole du catalogue au point cliqué ; sa fiche s'ouvre et désigne le champ « Nom ». */
  const create = (key: string, at: [number, number]) => {
    if (!guard(t("poser des annotations"))) return;
    if (items.length >= MAX_ANNOTATIONS) {
      notify({ text: t("{n} annotations au plus par dossier", { n: MAX_ANNOTATIONS }), tone: "warn" });
      return;
    }
    const id = newId();
    let name = "";
    mutation.mutate({
      apply: (current) => {
        const item = newAnnotation(key, at, current, id);
        name = nameOf(item);
        return [...current, item];
      },
      text: () => t("Annotation ajoutée : {name}", { name }),
      label: () => t("ajout de {name}", { name }),
      select: { id, at, focus: true },
    });
  };

  /** Modification de la fiche ; `what` arrive traduit (« Identité modifiée »). */
  const edit = (id: string, patch: Partial<Annotation>, what: string) => {
    if (refuseInherited(id) || !guard(t("modifier les annotations"))) return;
    // Nom relevé sur l'état au départ de l'écriture (et non au rendu du composant qui la demande).
    let name = t("annotation");
    mutation.mutate({
      apply: (current) =>
        current.map((a) => {
          if (a.id !== id) return a;
          const next = patched(a, patch);
          name = nameOf(next);
          return next;
        }),
      text: () => t("{what} : {name}", { what, name }),
      label: () => t("modification de {name}", { name }),
    });
  };

  /** Glissement sur la carte : position du temps affiché. */
  const move = (id: string, phase: Phase, at: [number, number], onSettled?: () => void) => {
    if (refuseInherited(id) || !guard(t("déplacer les annotations"))) {
      onSettled?.();
      return;
    }
    let name = t("annotation");
    mutation.mutate(
      {
        apply: (current) =>
          current.map((a) => {
            if (a.id !== id) return a;
            name = nameOf(a);
            return movedTo(a, phase, at);
          }),
        text: () => t("Annotation déplacée en {phase} : {name}", { phase, name }),
        label: () => t("déplacement de {name}", { name }),
      },
      { onSettled },
    );
  };

  const remove = (id: string) => {
    if (refuseInherited(id) || !guard(t("supprimer les annotations"))) return;
    let name = t("annotation");
    mutation.mutate({
      apply: (current) => {
        const found = current.find((a) => a.id === id);
        if (found) name = nameOf(found);
        return current.filter((a) => a.id !== id);
      },
      text: () => t("Annotation supprimée : {name}", { name }),
      label: () => t("suppression de {name}", { name }),
    });
  };

  return { items, canEdit, busy: mutation.isPending, guard, create, edit, move, remove, refuseInherited };
}
